import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Rocket,
  DollarSign,
  Calendar,
  Target,
  MapPin,
  Users,
  Search,
  X,
  TrendingUp,
  Eye,
  MousePointerClick,
  Pause,
  Play,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import socialService, { SocialPost } from '../../services/socialService';
import onboardingService, { BusinessProfile } from '../../services/onboardingService';
import { Content } from '../../types';

interface BoostConfig {
  budget: number;
  duration_days: number;
  goal: 'engagement' | 'traffic' | 'awareness' | 'leads';
  targeting: {
    age_min: number;
    age_max: number;
    genders: number[];
    locations: Array<{ key: string; name: string; type: string; country_code?: string }>;
    interests: Array<{ id: string; name: string; audience_size?: number }>;
  };
  ad_account_id?: string;
}

interface BoostStatus {
  status: string;
  budget?: number;
  duration_days?: number;
  goal?: string;
  targeting?: any;
  boosted_at?: string;
  end_date?: string;
  metrics?: {
    impressions: number;
    reach: number;
    clicks: number;
    spend: number;
    ctr: number;
    cpc: number;
  };
  canBoost?: boolean;
  campaign_id?: string;
  ad_id?: string;
  effective_status?: string;
}

interface BoostPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post?: SocialPost | null;
  contentItem?: Content | null;
  onBoostSuccess?: () => void;
}

const GOALS = [
  {
    id: 'engagement' as const,
    label: 'Engajamento',
    description: 'Mais curtidas, comentarios e compartilhamentos',
    icon: TrendingUp,
    color: 'text-pink-500 bg-pink-50 dark:bg-pink-900/20'
  },
  {
    id: 'traffic' as const,
    label: 'Trafego',
    description: 'Mais visitas ao seu site ou pagina',
    icon: MousePointerClick,
    color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20'
  },
  {
    id: 'awareness' as const,
    label: 'Reconhecimento',
    description: 'Alcance o maximo de pessoas',
    icon: Eye,
    color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20'
  },
  {
    id: 'leads' as const,
    label: 'Leads',
    description: 'Gere contatos e formularios',
    icon: Users,
    color: 'text-green-500 bg-green-50 dark:bg-green-900/20'
  }
];

const BUDGET_PRESETS = [35, 50, 100, 200, 350, 500];
const DURATION_PRESETS = [3, 5, 7, 10, 14, 30];

const BoostPostModal: React.FC<BoostPostModalProps> = ({
  isOpen,
  onClose,
  post,
  contentItem,
  onBoostSuccess
}) => {
  // Determine mode: social post or content
  const isContentMode = !!contentItem && !post;
  const { t } = useTranslation();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isBoosting, setIsBoosting] = useState(false);
  const [boostStatus, setBoostStatus] = useState<BoostStatus | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adAccounts, setAdAccounts] = useState<Array<{ id: string; account_id: string; name: string; currency: string }>>([]);
  const [contentBoostResult, setContentBoostResult] = useState<{ campaign_id: string; ad_id: string } | null>(null);
  const [previewImageBroken, setPreviewImageBroken] = useState(false);
  const [brandProfile, setBrandProfile] = useState<BusinessProfile | null>(null);
  // Quick audience preset. "advantage" = let Meta pick (minimal targeting);
  // "region" = pre-fill with BusinessProfile location (or BR fallback); "custom" = user edits.
  const [audiencePreset, setAudiencePreset] = useState<'advantage' | 'region' | 'custom'>('advantage');
  const [reachEstimate, setReachEstimate] = useState<{ lower: number; upper: number; error?: string } | null>(null);
  const [loadingReach, setLoadingReach] = useState(false);

  // Form state
  const [config, setConfig] = useState<BoostConfig>({
    budget: 50,
    duration_days: 7,
    goal: 'engagement',
    targeting: {
      age_min: 18,
      age_max: 65,
      genders: [],
      locations: [],
      interests: []
    }
  });

  // Search states
  const [interestSearch, setInterestSearch] = useState('');
  const [interestResults, setInterestResults] = useState<Array<{ id: string; name: string; audience_size?: number }>>([]);
  const [searchingInterests, setSearchingInterests] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationResults, setLocationResults] = useState<Array<{ key: string; name: string; type: string; country_code?: string; country_name?: string }>>([]);
  const [searchingLocations, setSearchingLocations] = useState(false);

  const itemId = post?.id || contentItem?.id;

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen && (post || contentItem)) {
      setConfig({
        budget: 50,
        duration_days: 7,
        goal: 'engagement',
        targeting: {
          age_min: 18,
          age_max: 65,
          genders: [],
          locations: [],
          interests: []
        }
      });
      setShowAdvanced(false);
      // Reset the broken-image flag each time the modal opens with a new post,
      // otherwise a previous broken preview would suppress a valid new one.
      setPreviewImageBroken(false);
      setInterestSearch('');
      setLocationSearch('');
      setInterestResults([]);
      setLocationResults([]);
      setBoostStatus(null);
      setContentBoostResult(null);
      setAudiencePreset('advantage');
      setReachEstimate(null);

      if (!isContentMode) {
        loadBoostStatus();
      }
      loadAdAccounts();
      // Load BusinessProfile only to personalize the description text under
      // the "Minha região" button. The actual targeting is built from scratch
      // when the user picks a preset — we don't auto-resolve profile strings
      // into Meta geo IDs because the free-text "São Paulo" the user typed
      // in onboarding isn't guaranteed to map 1:1 to a Meta location key.
      onboardingService.getProfile()
        .then(p => setBrandProfile(p))
        .catch(() => setBrandProfile(null));
    }
  }, [isOpen, itemId]);

  /**
   * Derive a targeting payload from the chosen preset.
   * - advantage: minimal — let Meta's Advantage+ figure it out
   * - region: age 18-65 + location from BusinessProfile (or BR fallback)
   * - custom: keep whatever the user has
   */
  const applyAudiencePreset = (preset: 'advantage' | 'region' | 'custom') => {
    setAudiencePreset(preset);
    if (preset === 'advantage') {
      setConfig(prev => ({
        ...prev,
        targeting: {
          age_min: 18,
          age_max: 65,
          genders: [],
          locations: [],
          interests: []
        }
      }));
    } else if (preset === 'region') {
      // BusinessProfile stores locations as free-text strings that the Meta
      // API can't consume directly (it needs ISO country codes or Meta location
      // keys from `/search?type=adgeolocation`). So we default to Brazil here
      // and expose the location autocomplete for the user to narrow down.
      // A future enhancement could pre-resolve the profile's text into a
      // valid Meta geo entry, but doing that silently risks sending bad data
      // to the Ads API — explicit is better.
      setConfig(prev => ({
        ...prev,
        targeting: {
          age_min: 18,
          age_max: 65,
          genders: [],
          locations: [{ key: 'BR', name: 'Brasil', type: 'country', country_code: 'BR' }],
          interests: []
        }
      }));
    }
    // custom: no-op, keep current targeting
  };

  // Debounced reach estimate — fires whenever targeting or ad account changes.
  useEffect(() => {
    if (!config.ad_account_id || !isOpen) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingReach(true);
      try {
        // Build the Meta targeting_spec shape the backend expects
        const targetingSpec: any = {
          age_min: config.targeting.age_min,
          age_max: config.targeting.age_max,
          geo_locations: {
            countries: config.targeting.locations
              .filter(l => l.type === 'country' && l.country_code)
              .map(l => l.country_code!) ||
              ['BR']
          }
        };
        // Default to BR if no country resolved
        if (targetingSpec.geo_locations.countries.length === 0) {
          targetingSpec.geo_locations.countries = ['BR'];
        }
        if (config.targeting.genders.length > 0) {
          targetingSpec.genders = config.targeting.genders;
        }
        if (config.targeting.interests.length > 0) {
          targetingSpec.interests = config.targeting.interests.map(i => ({ id: i.id, name: i.name }));
        }
        const response = await socialService.getBoostEstimatedReach({
          ad_account_id: config.ad_account_id!,
          targeting: targetingSpec
        });
        if (cancelled) return;
        if (response.success && response.data) {
          setReachEstimate({
            lower: response.data.users_lower_bound || 0,
            upper: response.data.users_upper_bound || 0,
            error: response.data.error
          });
        }
      } catch (err) {
        if (!cancelled) setReachEstimate({ lower: 0, upper: 0, error: 'network' });
      } finally {
        if (!cancelled) setLoadingReach(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [
    config.ad_account_id,
    config.targeting.age_min,
    config.targeting.age_max,
    config.targeting.genders,
    config.targeting.locations,
    config.targeting.interests,
    isOpen
  ]);

  const loadBoostStatus = useCallback(async () => {
    if (!post) return;
    setIsLoading(true);
    try {
      const response = await socialService.getBoostStatus(post.id);
      if (response.success && response.data) {
        setBoostStatus(response.data);
        // Pre-fill form if there's existing config
        if (response.data.budget) {
          setConfig(prev => ({
            ...prev,
            budget: response.data!.budget || prev.budget,
            duration_days: response.data!.duration_days || prev.duration_days,
            goal: (response.data!.goal as any) || prev.goal,
            targeting: response.data!.targeting || prev.targeting
          }));
        }
      }
    } catch (error) {
      console.error('Error loading boost status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [post]);

  const loadAdAccounts = async () => {
    try {
      const response = await socialService.getBoostAdAccounts();
      if (response.success && response.data) {
        setAdAccounts(response.data);
        if (response.data.length > 0 && !config.ad_account_id) {
          setConfig(prev => ({ ...prev, ad_account_id: response.data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error loading ad accounts:', error);
    }
  };

  // Search interests with debounce
  useEffect(() => {
    if (interestSearch.length < 2) {
      setInterestResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingInterests(true);
      try {
        const response = await socialService.searchBoostInterests(interestSearch);
        if (response.success) {
          setInterestResults(response.data || []);
        }
      } catch {
        // ignore
      } finally {
        setSearchingInterests(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [interestSearch]);

  // Search locations with debounce
  useEffect(() => {
    if (locationSearch.length < 2) {
      setLocationResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingLocations(true);
      try {
        const response = await socialService.searchBoostLocations(locationSearch);
        if (response.success) {
          setLocationResults(response.data || []);
        }
      } catch {
        // ignore
      } finally {
        setSearchingLocations(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [locationSearch]);

  const handleBoost = async () => {
    if (!post && !contentItem) return;

    if (config.budget < 1) {
      toast.error('O orcamento minimo e R$ 1,00');
      return;
    }
    if (config.duration_days < 1) {
      toast.error('A duracao minima e 1 dia');
      return;
    }
    if (config.budget / config.duration_days < 5) {
      toast.error(`O orcamento diario minimo da Meta e R$ 5,00. Aumente o orcamento ou reduza a duracao.`);
      return;
    }
    if (config.targeting.age_min > config.targeting.age_max) {
      toast.error('A idade minima nao pode ser maior que a idade maxima');
      return;
    }
    if (adAccounts.length === 0) {
      toast.error('Conecte uma conta Meta Ads primeiro');
      return;
    }

    setIsBoosting(true);
    try {
      let response;

      if (isContentMode && contentItem) {
        // Content mode: create ad from scratch
        response = await socialService.boostContent({
          text: contentItem.content || contentItem.excerpt || '',
          title: contentItem.title,
          image_url: contentItem.featured_image_url,
          budget: config.budget,
          duration_days: config.duration_days,
          goal: config.goal,
          targeting: config.targeting,
          ad_account_id: config.ad_account_id
        });
      } else if (post) {
        // Social post mode: boost existing post
        response = await socialService.boostPost(post.id, {
          budget: config.budget,
          duration_days: config.duration_days,
          goal: config.goal,
          targeting: config.targeting,
          ad_account_id: config.ad_account_id
        });
      }

      if (response?.success) {
        toast.success('Impulsionado com sucesso!');
        if (isContentMode && response.data) {
          setContentBoostResult({ campaign_id: response.data.campaign_id, ad_id: response.data.ad_id });
        }
        if (!isContentMode) await loadBoostStatus();
        onBoostSuccess?.();
      } else {
        toast.error(response?.message || 'Erro ao impulsionar');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Erro ao impulsionar');
    } finally {
      setIsBoosting(false);
    }
  };

  const handleStopBoost = async () => {
    if (!post) return;
    if (!confirm('Tem certeza que deseja pausar o impulsionamento?')) return;

    setIsBoosting(true);
    try {
      const response = await socialService.stopBoost(post.id);
      if (response.success) {
        toast.success('Impulsionamento pausado');
        await loadBoostStatus();
        onBoostSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao pausar');
    } finally {
      setIsBoosting(false);
    }
  };

  const handleResumeBoost = async () => {
    if (!post) return;
    setIsBoosting(true);
    try {
      const response = await socialService.resumeBoost(post.id);
      if (response.success) {
        toast.success('Impulsionamento retomado');
        await loadBoostStatus();
        onBoostSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao retomar');
    } finally {
      setIsBoosting(false);
    }
  };

  const addInterest = (interest: { id: string; name: string; audience_size?: number }) => {
    if (config.targeting.interests.find(i => i.id === interest.id)) return;
    setConfig(prev => ({
      ...prev,
      targeting: {
        ...prev.targeting,
        interests: [...prev.targeting.interests, interest]
      }
    }));
    setInterestSearch('');
    setInterestResults([]);
  };

  const removeInterest = (id: string) => {
    setConfig(prev => ({
      ...prev,
      targeting: {
        ...prev.targeting,
        interests: prev.targeting.interests.filter(i => i.id !== id)
      }
    }));
  };

  const addLocation = (location: { key: string; name: string; type: string; country_code?: string }) => {
    if (config.targeting.locations.find(l => l.key === location.key)) return;
    setConfig(prev => ({
      ...prev,
      targeting: {
        ...prev.targeting,
        locations: [...prev.targeting.locations, location]
      }
    }));
    setLocationSearch('');
    setLocationResults([]);
  };

  const removeLocation = (key: string) => {
    setConfig(prev => ({
      ...prev,
      targeting: {
        ...prev.targeting,
        locations: prev.targeting.locations.filter(l => l.key !== key)
      }
    }));
  };

  const dailyBudget = config.duration_days > 0 ? (config.budget / config.duration_days).toFixed(2) : '0.00';

  const isActive = boostStatus?.status === 'active';
  const isPaused = boostStatus?.status === 'paused';
  const isPending = boostStatus?.status === 'pending';
  const isCompleted = boostStatus?.status === 'completed';
  const isRejected = boostStatus?.status === 'rejected';
  const isFailed = boostStatus?.status === 'failed';
  const hasActiveBoost = isActive || isPaused || isPending;
  const hasEndedBoost = isCompleted || isRejected || isFailed;
  const hasContentBoost = isContentMode && !!contentBoostResult;
  const showForm = (isContentMode && !hasContentBoost) || (!isContentMode && !hasActiveBoost && !hasEndedBoost);

  const renderStatusBadge = () => {
    if (!boostStatus || boostStatus.status === 'none') return null;

    const badges: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      active: { color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', icon: <Rocket className="w-3.5 h-3.5" />, label: 'Ativo' },
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300', icon: <Clock className="w-3.5 h-3.5" />, label: 'Pendente' },
      paused: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300', icon: <Pause className="w-3.5 h-3.5" />, label: 'Pausado' },
      completed: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300', icon: <CheckCircle className="w-3.5 h-3.5" />, label: 'Concluido' },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', icon: <AlertCircle className="w-3.5 h-3.5" />, label: 'Rejeitado' },
      failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', icon: <AlertCircle className="w-3.5 h-3.5" />, label: 'Falhou' }
    };

    const badge = badges[boostStatus.status];
    if (!badge) return null;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${badge.color}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const renderMetrics = () => {
    if (!boostStatus?.metrics) return null;
    const m = boostStatus.metrics;

    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Desempenho do Impulsionamento
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{(m.impressions || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Impressoes</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{(m.reach || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Alcance</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{(m.clicks || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Cliques</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">R$ {(m.spend || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500">Gasto</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{(m.ctr || 0).toFixed(2)}%</p>
            <p className="text-xs text-gray-500">CTR</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">R$ {(m.cpc || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500">CPC</p>
          </div>
        </div>

        {boostStatus.boosted_at && (
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-200 dark:border-gray-700">
            <span>Inicio: {new Date(boostStatus.boosted_at).toLocaleDateString('pt-BR')}</span>
            {boostStatus.end_date && <span>Fim: {new Date(boostStatus.end_date).toLocaleDateString('pt-BR')}</span>}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Impulsionar Post" size="lg" maxHeight>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Impulsionar Post" size="lg" maxHeight>
      <div className="space-y-6">
        {/* Preview */}
        {(post || contentItem) && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
            <div className="flex gap-3">
              {(() => {
                // Pick the first URL that actually looks like an image — skip .mp4/.mov
                // (they used to render as a broken <img> when the post was a reels).
                const videoExts = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '/video/', '/videos/'];
                const candidate = (post?.media_urls || []).find((u) => {
                  if (!u) return false;
                  const lower = u.toLowerCase();
                  return !videoExts.some((ext) => lower.includes(ext));
                }) || contentItem?.featured_image_url;

                if (!candidate || previewImageBroken) return null;
                return (
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-gray-700">
                    <img
                      src={candidate}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={() => setPreviewImageBroken(true)}
                    />
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0">
                {contentItem && <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">{contentItem.title}</p>}
                <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
                  {post?.content || contentItem?.excerpt || contentItem?.content || ''}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {post?.platforms?.map(p => (
                    <span key={p} className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
                      {p}
                    </span>
                  ))}
                  {contentItem && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-600 dark:text-blue-300">
                      {contentItem.type.replace('_', ' ')}
                    </span>
                  )}
                  {!isContentMode && renderStatusBadge()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Boost Active - Show Controls */}
        {hasContentBoost && contentBoostResult && (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Impulsionamento ativo!</p>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400">
                Campanha: {contentBoostResult.campaign_id}
              </p>
            </div>

            <Button
              variant="secondary"
              onClick={async () => {
                setIsBoosting(true);
                try {
                  await socialService.stopContentBoost(contentBoostResult.campaign_id);
                  toast.success('Impulsionamento pausado');
                  setContentBoostResult(null);
                } catch (error: any) {
                  toast.error(error.response?.data?.message || 'Erro ao pausar');
                } finally {
                  setIsBoosting(false);
                }
              }}
              loading={isBoosting}
              className="w-full gap-2"
            >
              <Pause className="w-4 h-4" />
              Pausar Impulsionamento
            </Button>
          </div>
        )}

        {/* Active/Paused Boost - Show Metrics & Controls (SocialPost mode only) */}
        {!isContentMode && hasActiveBoost && (
          <div className="space-y-4">
            {renderMetrics()}

            <div className="flex gap-3">
              {isActive && (
                <Button
                  variant="secondary"
                  onClick={handleStopBoost}
                  loading={isBoosting}
                  className="flex-1 gap-2"
                >
                  <Pause className="w-4 h-4" />
                  Pausar Impulsionamento
                </Button>
              )}
              {isPaused && (
                <Button
                  variant="primary"
                  onClick={handleResumeBoost}
                  loading={isBoosting}
                  className="flex-1 gap-2"
                >
                  <Play className="w-4 h-4" />
                  Retomar Impulsionamento
                </Button>
              )}
            </div>

            {boostStatus?.budget && (
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <p>Orcamento: R$ {Number(boostStatus.budget).toFixed(2)} / {boostStatus.duration_days} dias</p>
                <p>Objetivo: {GOALS.find(g => g.id === boostStatus.goal)?.label || boostStatus.goal}</p>
              </div>
            )}
          </div>
        )}

        {/* Completed/Rejected Boost (SocialPost mode only) */}
        {!isContentMode && (isCompleted || isRejected || isFailed) && (
          <div className="space-y-4">
            {renderMetrics()}

            {isRejected && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  O impulsionamento foi rejeitado pela Meta. Verifique se o conteudo segue as politicas de publicidade.
                </p>
              </div>
            )}

            {isFailed && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Houve um erro ao impulsionar. Tente novamente.
                </p>
              </div>
            )}

            <Button
              variant="primary"
              onClick={() => setBoostStatus({ status: 'none', canBoost: true })}
              className="w-full gap-2"
            >
              <Rocket className="w-4 h-4" />
              Impulsionar Novamente
            </Button>
          </div>
        )}

        {/* Boost Form */}
        {showForm && (
          <div className="space-y-6">
            {/* No Meta Ads account warning */}
            {adAccounts.length === 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Conecte uma conta Meta Ads na aba de contas para impulsionar posts.
                </p>
              </div>
            )}

            {/* Ad Account Selection */}
            {adAccounts.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Conta de Anuncios
                </label>
                <select
                  value={config.ad_account_id || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, ad_account_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  {adAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.currency})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Audience preset — the most important decision. Without it,
                boost campaigns go with Meta's account defaults (often worldwide,
                all ages) and burn budget. Keeps "custom" for power users who
                want to touch every knob. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Público-alvo
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {([
                  {
                    id: 'advantage' as const,
                    label: 'Seguidores e similares',
                    description: 'Meta decide quem mais vai ver (Advantage+)',
                    icon: TrendingUp
                  },
                  {
                    id: 'region' as const,
                    label: 'Brasil (ajustável)',
                    description: 'Começa com Brasil inteiro, você pode refinar por estado/cidade abaixo',
                    icon: MapPin
                  },
                  {
                    id: 'custom' as const,
                    label: 'Personalizado',
                    description: 'Editar idade, local, interesses',
                    icon: Target
                  }
                ]).map(preset => {
                  const PIcon = preset.icon;
                  const isSelected = audiencePreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyAudiencePreset(preset.id)}
                      className={`flex items-start gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <PIcon className={`w-4 h-4 mt-0.5 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{preset.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{preset.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Inline editing: always shows age slider + chips of selected
                  locations. "Personalizado" preset exposes gender + location
                  search right here without needing to open advanced. */}
              <div className="mt-3 space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/40">
                {/* Age range slider (dual) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Faixa etária: {config.targeting.age_min}–{config.targeting.age_max} anos
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="18"
                      max="65"
                      value={config.targeting.age_min}
                      onChange={(e) => {
                        const v = Math.min(Number(e.target.value), config.targeting.age_max - 1);
                        setConfig(prev => ({ ...prev, targeting: { ...prev.targeting, age_min: v } }));
                        if (audiencePreset !== 'custom') setAudiencePreset('custom');
                      }}
                      className="flex-1 accent-primary-500"
                    />
                    <input
                      type="range"
                      min="18"
                      max="65"
                      value={config.targeting.age_max}
                      onChange={(e) => {
                        const v = Math.max(Number(e.target.value), config.targeting.age_min + 1);
                        setConfig(prev => ({ ...prev, targeting: { ...prev.targeting, age_max: v } }));
                        if (audiencePreset !== 'custom') setAudiencePreset('custom');
                      }}
                      className="flex-1 accent-primary-500"
                    />
                  </div>
                </div>

                {/* Location chips — visible when not Advantage+ */}
                {audiencePreset !== 'advantage' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      Localização
                    </label>
                    {config.targeting.locations.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {config.targeting.locations.map(loc => (
                          <span
                            key={loc.key}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-xs text-gray-700 dark:text-gray-300"
                          >
                            <MapPin className="w-3 h-3" />
                            {loc.name}
                            <button
                              type="button"
                              onClick={() => {
                                removeLocation(loc.key);
                                if (audiencePreset !== 'custom') setAudiencePreset('custom');
                              }}
                              className="hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        placeholder="Buscar país, estado ou cidade..."
                        className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                      {searchingLocations && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
                      )}
                    </div>
                    {locationResults.length > 0 && (
                      <div className="mt-1 max-h-32 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                        {locationResults.map(loc => (
                          <button
                            key={loc.key}
                            type="button"
                            onClick={() => {
                              addLocation(loc);
                              if (audiencePreset !== 'custom') setAudiencePreset('custom');
                              setLocationSearch('');
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-900 dark:text-white">{loc.name}</span>
                            {loc.country_name && <span className="text-xs text-gray-500">({loc.country_name})</span>}
                            <span className="text-xs text-gray-400 ml-auto">{loc.type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Gender — visible only in custom */}
                {audiencePreset === 'custom' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Gênero
                    </label>
                    <div className="flex gap-2">
                      {[
                        { value: [] as number[], label: 'Todos' },
                        { value: [1], label: 'Masculino' },
                        { value: [2], label: 'Feminino' }
                      ].map(opt => (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => setConfig(prev => ({ ...prev, targeting: { ...prev.targeting, genders: opt.value } }))}
                          className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                            JSON.stringify(config.targeting.genders) === JSON.stringify(opt.value)
                              ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Goal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Objetivo do Impulsionamento
              </label>
              <div className="grid grid-cols-2 gap-2">
                {GOALS.map((goal) => {
                  const Icon = goal.icon;
                  const isSelected = config.goal === goal.id;
                  return (
                    <button
                      key={goal.id}
                      onClick={() => setConfig(prev => ({ ...prev, goal: goal.id }))}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg ${goal.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{goal.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{goal.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Orcamento Total (R$)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {BUDGET_PRESETS.map(amount => (
                  <button
                    key={amount}
                    onClick={() => setConfig(prev => ({ ...prev, budget: amount }))}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      config.budget === amount
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    R$ {amount}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                step="1"
                value={config.budget}
                onChange={(e) => setConfig(prev => ({ ...prev, budget: Math.max(1, Number(e.target.value)) }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                placeholder="Valor do orcamento"
              />
              <p className={`text-xs mt-1 ${Number(dailyBudget) < 5 ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                ~R$ {dailyBudget}/dia {Number(dailyBudget) < 5 ? '(minimo R$ 5,00/dia)' : ''}
              </p>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />
                Duracao (dias)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {DURATION_PRESETS.map(days => (
                  <button
                    key={days}
                    onClick={() => setConfig(prev => ({ ...prev, duration_days: days }))}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      config.duration_days === days
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    {days} {days === 1 ? 'dia' : 'dias'}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                max="90"
                value={config.duration_days}
                onChange={(e) => setConfig(prev => ({ ...prev, duration_days: Math.max(1, Math.min(90, Number(e.target.value))) }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Advanced Targeting Toggle — now contains ONLY interests,
                since age/gender/location were promoted to the audience preset
                panel above. */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700"
            >
              <Target className="w-4 h-4" />
              Interesses detalhados
              {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showAdvanced && (
              <div className="space-y-4 pl-2 border-l-2 border-primary-200 dark:border-primary-800">
                {/* Interests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <Target className="w-4 h-4 inline mr-1" />
                    Interesses
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={interestSearch}
                      onChange={(e) => setInterestSearch(e.target.value)}
                      placeholder="Buscar interesses..."
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    />
                    {searchingInterests && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  {interestResults.length > 0 && (
                    <div className="mt-1 max-h-32 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                      {interestResults.map(interest => (
                        <button
                          key={interest.id}
                          onClick={() => addInterest(interest)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                        >
                          <span className="text-gray-900 dark:text-white">{interest.name}</span>
                          {interest.audience_size && (
                            <span className="text-xs text-gray-500">
                              {interest.audience_size > 1000000
                                ? `${(interest.audience_size / 1000000).toFixed(1)}M`
                                : `${(interest.audience_size / 1000).toFixed(0)}K`
                              } pessoas
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {config.targeting.interests.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {config.targeting.interests.map(interest => (
                        <span
                          key={interest.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 dark:bg-primary-900/30 rounded-full text-xs text-primary-700 dark:text-primary-300"
                        >
                          {interest.name}
                          <button onClick={() => removeInterest(interest.id)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-primary-100 dark:border-primary-800">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Resumo</h4>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Orçamento total:</span>
                  <span className="font-medium text-gray-900 dark:text-white">R$ {config.budget.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Duração:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{config.duration_days} dias</span>
                </div>
                <div className="flex justify-between">
                  <span>Orçamento diário:</span>
                  <span className="font-medium text-gray-900 dark:text-white">~R$ {dailyBudget}</span>
                </div>
                <div className="flex justify-between">
                  <span>Objetivo:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{GOALS.find(g => g.id === config.goal)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span>Público:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {audiencePreset === 'advantage' ? 'Seguidores e similares' :
                     audiencePreset === 'region' ? 'Minha região' : 'Personalizado'}
                  </span>
                </div>
                {config.targeting.locations.length > 0 && (
                  <div className="flex justify-between">
                    <span>Locais:</span>
                    <span className="font-medium text-gray-900 dark:text-white truncate ml-2 max-w-[60%] text-right" title={config.targeting.locations.map(l => l.name).join(', ')}>
                      {config.targeting.locations.map(l => l.name).join(', ')}
                    </span>
                  </div>
                )}

                {/* Estimated reach — computed by Meta based on current targeting */}
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-primary-100 dark:border-primary-800/50">
                  <span className="inline-flex items-center gap-1">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Alcance estimado (dia):
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {loadingReach ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                    ) : reachEstimate?.error ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400" title={reachEstimate.error}>
                        — (não calculado)
                      </span>
                    ) : reachEstimate && (reachEstimate.lower > 0 || reachEstimate.upper > 0) ? (
                      `${reachEstimate.lower.toLocaleString('pt-BR')}–${reachEstimate.upper.toLocaleString('pt-BR')} pessoas`
                    ) : (
                      <span className="text-xs text-gray-400">calculando...</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Boost Button */}
            <Button
              variant="primary"
              onClick={handleBoost}
              loading={isBoosting}
              disabled={adAccounts.length === 0 || isBoosting}
              className="w-full gap-2 bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700"
            >
              <Rocket className="w-4 h-4" />
              Impulsionar Post
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BoostPostModal;
